/**
 * Distribute / Ramp sheet (W2-11 batch-tools; DESIGN.md §4.14, §5.8; RPE MultiNote / MultiEvent E§3.12, E§4.9 with
 * the errata fixed). Live ghosts through `useToolGesture`; the equivalent expression is shown with a Copy button
 * so pros learn the inline form; the first items are listed "before -> after". Parameters are remembered per user.
 */

import { useMemo, useState } from 'react';
import type { DialogProps } from '../../../app/registry/types';
import { LINEAR } from '../../../core/ease/index';
import type { Ease } from '../../../core/ease/types';
import { randomSeed } from '../../../core/math/rng';
import type { ChartDoc, Key, KeyId, Note, NoteId } from '../../../core/model/types';
import { Beats } from '../../../core/time/beat';
import { keyTimeOrder, noteTimeOrder, noteXOrder } from '../../../core/tools/common';
import { Tools } from '../../../core/tools/index';
import type { DistributeMode, DistributeParams, DistributeTarget, ToolReport } from '../../../core/tools/types';
import { Button } from '../../../ui/Button';
import { IconButton } from '../../../ui/IconButton';
import { Segmented } from '../../../ui/Segmented';
import { Select } from '../../../ui/Select';
import { TextField } from '../../../ui/TextField';
import type { Json } from '../../../core/util/types';
import { m } from '../batch.messages';
import s from '../Batch.module.css';
import { EXT, extObject, isNum, isStr, pick, ref, remember, toastNotes } from '../batchContext';
import { CurveField, NumberInput, ToolSheet, useText, type SheetStatus, type Text } from '../sheetKit';
import { useToolGesture } from '../useToolGesture';

type NoteField = Extract<DistributeTarget, { kind: 'notes' }>['field'];
type KeyField = Extract<DistributeTarget, { kind: 'keys' }>['field'];

const NOTE_FIELDS: readonly NoteField[] = ['x', 'speed', 'size', 'yOffset', 'visibleTime', 'alpha', 'time'];
const KEY_FIELDS: readonly KeyField[] = ['value', 'valueIn', 'valueOut', 'time', 'easing'];
const MODES: readonly DistributeMode[] = ['set', 'add', 'multiply', 'max', 'min', 'mirror'];

const NOTE_FIELD_LABEL: Readonly<Record<NoteField, string>> = {
  x: m.fieldX,
  speed: m.fieldSpeed,
  size: m.fieldSize,
  yOffset: m.fieldYOffset,
  visibleTime: m.fieldVisibleTime,
  alpha: m.fieldAlpha,
  time: m.fieldTime,
};
const KEY_FIELD_LABEL: Readonly<Record<KeyField, string>> = {
  value: m.fieldValue,
  valueIn: m.fieldValueIn,
  valueOut: m.fieldValueOut,
  time: m.fieldTime,
  easing: m.fieldEasing,
};
const MODE_LABEL: Readonly<Record<DistributeMode, string>> = {
  set: m.modeSet,
  add: m.modeAdd,
  multiply: m.modeMultiply,
  max: m.modeMax,
  min: m.modeMin,
  mirror: m.modeMirror,
};

/** Pattern text ("1 -1", "1, -1, 0.5") to numbers; null when a part is not a number. */
export function parsePattern(textValue: string): number[] | null {
  const parts = textValue
    .split(/[\s,;]+/)
    .map((p) => p.trim())
    .filter((p) => p !== '');
  const out = parts.map(Number);
  return out.every((v) => Number.isFinite(v)) ? out : null;
}

/** Current value of an item for the preview list. */
function itemValue(doc: ChartDoc, target: DistributeTarget, id: number): number | null {
  if (target.kind === 'notes') {
    const n = doc.notes.get(id as NoteId);
    if (n === undefined) return null;
    switch (target.field) {
      case 'time':
        return Beats.toF64(n.t);
      case 'visibleTime':
        return n.visibleTime;
      default:
        return n[target.field];
    }
  }
  const k = doc.keys.get(id as KeyId);
  if (k === undefined) return null;
  switch (target.field) {
    case 'value':
    case 'valueOut':
      return typeof k.vOut === 'number' ? k.vOut : null;
    case 'valueIn':
      return typeof k.vIn === 'number' ? k.vIn : null;
    case 'time':
      return Beats.toF64(k.t);
    case 'easing':
      return k.ease.kind === 'tween' ? k.ease.id : 1;
  }
}

/** Items listed "before -> after" under the sheet. */
export const PREVIEW_COUNT = 6;

/**
 * The items in the order Distribute ramps them (core/tools/distribute.ts): notes by time or by X, keys of numeric
 * tracks by time (the tool skips the others), each id once; ids missing from the document are left out.
 */
export function itemOrder(
  doc: ChartDoc | null,
  notes: readonly NoteId[],
  keys: readonly KeyId[],
): { readonly time: readonly NoteId[]; readonly x: readonly NoteId[]; readonly keys: readonly KeyId[] } {
  if (doc === null) return { time: [], x: [], keys: [] };
  const ns: Note[] = [];
  for (const id of new Set(notes)) {
    const n = doc.notes.get(id);
    if (n !== undefined) ns.push(n);
  }
  const ks: Key[] = [];
  for (const id of new Set(keys)) {
    const k = doc.keys.get(id);
    if (k !== undefined && typeof k.vOut === 'number') ks.push(k);
  }
  return {
    time: [...ns].sort(noteTimeOrder).map((n) => n.id),
    x: [...ns].sort(noteXOrder).map((n) => n.id),
    keys: ks.sort(keyTimeOrder).map((k) => k.id),
  };
}

function fmt(text: Text, v: number | null): string {
  return v === null ? text.t(m.valueNone) : String(Number(v.toPrecision(6)));
}

export function DistributeSheet(p: DialogProps<'distribute'>) {
  const { services, props } = p;
  const text = useText();
  const store = services.store;
  const notes = useMemo(() => props.notes.filter((id) => store?.doc.notes.has(id) === true), [props.notes, store]);
  // Keys selected as points and segment start keys work alike here (value, arrive, leave, time, curve).
  const keys = useMemo(
    () => [...new Set([...(props.keys ?? []), ...props.segments])].filter((id) => store?.doc.keys.has(id) === true),
    [props.keys, props.segments, store],
  );
  const saved = useMemo(() => extObject(services, EXT.distribute), [services]);

  const [kind, setKind] = useState<'notes' | 'keys'>(notes.length > 0 ? 'notes' : 'keys');
  const [noteField, setNoteField] = useState<NoteField>(() => {
    const v = pick(saved, 'noteField', isStr, 'x');
    return (NOTE_FIELDS as readonly string[]).includes(v) ? (v as NoteField) : 'x';
  });
  const [keyField, setKeyField] = useState<KeyField>(() => {
    const v = pick(saved, 'keyField', isStr, 'value');
    return (KEY_FIELDS as readonly string[]).includes(v) ? (v as KeyField) : 'value';
  });
  const [from, setFrom] = useState(() => pick(saved, 'from', isNum, -400));
  const [to, setTo] = useState(() => pick(saved, 'to', isNum, 400));
  const [curve, setCurve] = useState<Ease>(LINEAR);
  const [patternText, setPatternText] = useState(() => pick(saved, 'pattern', isStr, '1'));
  const [jitter, setJitter] = useState(() => pick(saved, 'jitter', isNum, 0));
  const [seed, setSeed] = useState(() => pick(saved, 'seed', isNum, 42));
  const [mode, setMode] = useState<DistributeMode>(() => {
    const v = pick(saved, 'mode', isStr, 'set');
    return (MODES as readonly string[]).includes(v) ? (v as DistributeMode) : 'set';
  });
  const [order, setOrder] = useState<'time' | 'x'>('time');

  const pattern = useMemo(() => parsePattern(patternText), [patternText]);
  const target = useMemo<DistributeTarget | null>(() => {
    if (kind === 'notes') return notes.length > 0 ? { kind: 'notes', ids: notes, field: noteField } : null;
    return keys.length > 0 ? { kind: 'keys', ids: keys, field: keyField } : null;
  }, [kind, notes, keys, noteField, keyField]);
  const params = useMemo<DistributeParams | null>(
    () =>
      target === null || pattern === null
        ? null
        : { target, from, to, curve, pattern, jitter, seed, mode, order: kind === 'notes' ? order : 'time' },
    [target, pattern, from, to, curve, jitter, seed, mode, order, kind],
  );

  // "Before" values and the order the tool ramps the items in, read once before the preview changes the document
  // (a ramp of time or X reorders the items; the tool always sorts the document as it was).
  const [initial] = useState(() => {
    const values = new Map<string, number | null>();
    if (store === null) return { values, order: itemOrder(null, notes, keys) };
    for (const f of NOTE_FIELDS)
      for (const id of notes)
        values.set(`n${f}:${String(id)}`, itemValue(store.doc, { kind: 'notes', ids: [], field: f }, id));
    for (const f of KEY_FIELDS)
      for (const id of keys)
        values.set(`k${f}:${String(id)}`, itemValue(store.doc, { kind: 'keys', ids: [], field: f }, id));
    return { values, order: itemOrder(store.doc, notes, keys) };
  });

  const build = useMemo(
    () => (params === null ? null : (tx: Parameters<typeof Tools.distribute>[0]) => Tools.distribute(tx, params)),
    [params],
  );
  const gesture = useToolGesture<ToolReport>({
    services,
    label: ref(m.txnDistribute),
    command: 'tools.distribute',
    build,
  });

  const expression = params === null ? '' : Tools.distributeExpression(params);
  const outcome = gesture.outcome;
  let status: SheetStatus = { kind: 'none' };
  if (pattern === null) status = { kind: 'error', text: text.t(m.patternInvalid) };
  else if (gesture.status === 'paused') status = { kind: 'warn', text: text.t(m.previewPaused) };
  else if (outcome !== null && !outcome.ok) status = { kind: 'error', text: text.tr(outcome.reason) };
  else if (outcome?.ok === true)
    status = { kind: 'ok', text: text.t(m.distributeChanged, { count: outcome.value.changed }) };

  // The first items in the order the ramp gives them values (item 1 gets `from`).
  const previewItems = (() => {
    if (store === null || target === null) return [];
    const ids = target.kind === 'notes' ? (order === 'x' ? initial.order.x : initial.order.time) : initial.order.keys;
    return ids.slice(0, PREVIEW_COUNT).map((id) => {
      const key = `${target.kind === 'notes' ? 'n' : 'k'}${target.field}:${String(id)}`;
      return { id, before: initial.values.get(key) ?? null, after: itemValue(store.doc, target, id) };
    });
  })();

  const persist = (): void => {
    const value: Json = { noteField, keyField, from, to, pattern: patternText, jitter, seed, mode };
    remember(services, EXT.distribute, value);
  };
  const apply = (): void => {
    const out = gesture.apply();
    if (out === null || !out.ok) return;
    persist();
    services.ui.toast(ref(m.distributeDone, { count: out.value.changed }), { kind: 'success', undo: out.txn !== null });
    toastNotes(services, out.notes);
    p.close({ applied: true });
  };
  const cancel = (): void => {
    gesture.cancel();
    p.close(null);
  };
  const copy = (): void => {
    void navigator.clipboard.writeText(expression).then(
      () => {
        services.ui.toast(ref(m.expressionCopied), { kind: 'info' });
      },
      () => {
        services.ui.toast(ref(m.copyFailed), { kind: 'error' });
      },
    );
  };

  const subtitle =
    notes.length > 0 && keys.length > 0
      ? text.t(m.countNotesKeys, { notes: notes.length, keys: keys.length })
      : notes.length > 0
        ? text.t(m.countNotes, { count: notes.length })
        : text.t(m.countKeys, { count: keys.length });

  return (
    <ToolSheet
      title={text.t(m.distributeTitle)}
      subtitle={subtitle}
      status={status}
      applyLabel={text.t(m.apply)}
      applyDisabledReason={params === null ? ref(m.distributeNeedsItems) : null}
      onApply={apply}
      onCancel={cancel}
    >
      {notes.length > 0 && keys.length > 0 && (
        <Segmented<'notes' | 'keys'>
          label={text.t(m.applyTo)}
          value={kind}
          options={[
            { value: 'notes', label: text.t(m.targetNotes) },
            { value: 'keys', label: text.t(m.targetKeys) },
          ]}
          onChange={setKind}
        />
      )}
      {kind === 'notes' ? (
        <div className={s.row}>
          <Select<NoteField>
            label={text.t(m.change)}
            value={noteField}
            options={NOTE_FIELDS.map((f) => ({ value: f, label: text.t(NOTE_FIELD_LABEL[f]) }))}
            onChange={setNoteField}
          />
          <Segmented<'time' | 'x'>
            label={text.t(m.order)}
            value={order}
            options={[
              { value: 'time', label: text.t(m.orderTime) },
              { value: 'x', label: text.t(m.orderX) },
            ]}
            onChange={setOrder}
          />
        </div>
      ) : (
        <Select<KeyField>
          label={text.t(m.change)}
          value={keyField}
          options={KEY_FIELDS.map((f) => ({ value: f, label: text.t(KEY_FIELD_LABEL[f]) }))}
          onChange={setKeyField}
        />
      )}
      <div className={s.row}>
        <NumberInput label={text.t(m.from)} value={from} onChange={setFrom} />
        <NumberInput label={text.t(m.to)} value={to} onChange={setTo} />
      </div>
      <CurveField services={services} label={text.t(m.curve)} value={curve} onChange={setCurve} />
      <TextField
        label={text.t(m.pattern)}
        value={patternText}
        onCommit={setPatternText}
        placeholder={ref(m.patternPlaceholder)}
      />
      <div className={s.row}>
        <NumberInput label={text.t(m.jitter)} value={jitter} min={0} onChange={setJitter} />
        <div className={s.rowTight}>
          <NumberInput label={text.t(m.seed)} value={seed} integer min={0} max={4294967295} onChange={setSeed} />
          <IconButton
            icon="RefreshCw"
            label={ref(m.newSeed)}
            onClick={() => {
              setSeed(randomSeed());
            }}
          />
        </div>
      </div>
      <Segmented<DistributeMode>
        label={text.t(m.how)}
        value={mode}
        options={MODES.map((v) => ({ value: v, label: text.t(MODE_LABEL[v]) }))}
        onChange={setMode}
      />
      {expression !== '' && (
        <div className={s.expression}>
          <pre className={s.code} aria-label={text.t(m.equivalentExpression)}>
            {expression}
          </pre>
          <Button variant="ghost" size="sm" icon="Copy" onClick={copy}>
            {text.t(m.copy)}
          </Button>
        </div>
      )}
      {kind === 'notes' && order === 'x' && <p className={s.hint}>{text.t(m.expressionTimeOrder)}</p>}
      {previewItems.length > 0 && (
        <ul className={s.preview} aria-label={text.t(m.previewList)}>
          {previewItems.map((it) => (
            <li key={it.id}>
              {text.t(m.previewItem, { id: it.id, before: fmt(text, it.before), after: fmt(text, it.after) })}
            </li>
          ))}
        </ul>
      )}
    </ToolSheet>
  );
}
